import { mercatorPoint } from './replayGeometry';
import type { RoutePoint } from '../../../convex/lib/gpsMeasurement';
export type ReplayPoint = RoutePoint & { savedAt?: number; heldPositionBefore?: boolean };
const movingGapMs = 30_000;
const idleUpdateMs = 330_000; // Five-minute source cadence plus delivery allowance.
function metres(a: RoutePoint, b: RoutePoint) {
  return Math.hypot((a.longitude - b.longitude) * Math.cos((a.latitude + b.latitude) * Math.PI / 360), a.latitude - b.latitude) * 111195;
}
export function prepareReplayRoute(points: RoutePoint[]): ReplayPoint[] {
  const ordered = points.filter(p => Number.isFinite(p.latitude) && Number.isFinite(p.longitude) && Math.abs(p.latitude) <= 90 && Math.abs(p.longitude) <= 180)
    .map(p => ({ ...p, savedAt: p.timestamp, timestamp: Number.isFinite(p.gpsMeasuredAt) && p.gpsMeasuredAt! > 0 ? p.gpsMeasuredAt! : p.timestamp }))
    .filter(p => Number.isFinite(p.timestamp)).sort((a, b) => a.timestamp - b.timestamp);
  const result: ReplayPoint[] = [];
  for (const point of ordered) {
    const previous = result[result.length - 1];
    if (previous?.timestamp === point.timestamp) continue;
    const span = previous ? point.timestamp - previous.timestamp : 0;
    const distance = previous ? metres(previous, point) : 0;
    const measured = previous && Number.isFinite(previous.gpsMeasuredAt) && Number.isFinite(point.gpsMeasuredAt);
    const idle = previous && previous.speed !== undefined && previous.speed >= 0 && previous.speed <= .5 &&
      point.speed !== undefined && point.speed >= 0 && point.speed <= .5 && distance <= 5;
    const confirmedStop = idle && point.observationStartedAt !== undefined && point.observationStartedAt <= previous!.timestamp;
    const held = measured && idle && !confirmedStop && span > movingGapMs && span <= idleUpdateMs;
    const teleport = previous && span > 0 && distance / (span / 1000) > 60;
    // Stored gapBefore used a 15s threshold. Reclassify measured intervals in
    // the reader; do not rewrite records or apply that threshold to legacy data.
    const gap = Boolean(teleport || (measured
      ? span > movingGapMs && !confirmedStop
      : point.gapBefore));
    result.push({ ...point, gapBefore: gap, heldPositionBefore: Boolean(held && !teleport) });
  }
  return result;
}
export function replaySegmentIndex(time: number, route: RoutePoint[]) {
  let lo = 0, hi = Math.max(0, route.length - 2);
  while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (route[mid].timestamp <= time) lo = mid; else hi = mid - 1; }
  return lo;
}
export function replayPosition(offset: number, route: ReplayPoint[]): [number, number] | null {
  if (!route.length) return null;
  const time = route[0].timestamp + offset;
  const last = route[route.length - 1];
  if (time >= last.timestamp) return [last.longitude, last.latitude];
  const i = replaySegmentIndex(time, route), a = route[i], b = route[i + 1];
  if (!b || time <= a.timestamp) return [a.longitude, a.latitude];
  if (b.heldPositionBefore) return [a.longitude, a.latitude];
  if (b.gapBefore) return null;
  if (a.speed !== undefined && a.speed <= .5 && b.speed !== undefined && b.speed <= .5 && metres(a,b) <= 5) return [a.longitude, a.latitude];
  const t = Math.max(0, Math.min(1, (time - a.timestamp) / (b.timestamp - a.timestamp)));
  // Match the projected straight segment drawn by Mapbox, including at pitch.
  const start = mercatorPoint([a.longitude, a.latitude]);
  const end = mercatorPoint([b.longitude, b.latitude]);
  const x = start[0] + (end[0] - start[0]) * t;
  const y = start[1] + (end[1] - start[1]) * t;
  return [x * 360 - 180, Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI];
}
export function replayLocationStatus(offset: number, route: ReplayPoint[]): { kind: 'held' | 'interpolated'; ageMs: number } | null {
  if (route.length < 2) return null;
  const time = route[0].timestamp + offset;
  const i = replaySegmentIndex(time, route), a = route[i], b = route[i + 1];
  if (!b || time <= a.timestamp || time >= b.timestamp) return null;
  if (b.heldPositionBefore) return { kind: 'held', ageMs: time - a.timestamp };
  if (!b.gapBefore && !b.stopEndpoint && b.timestamp - a.timestamp > 15_000)
    return { kind: 'interpolated', ageMs: time - a.timestamp };
  return null;
}

function bearing(a: RoutePoint, b: RoutePoint) {
  const r = Math.PI / 180, d = (b.longitude - a.longitude) * r;
  return (Math.atan2(Math.sin(d) * Math.cos(b.latitude*r), Math.cos(a.latitude*r)*Math.sin(b.latitude*r)-Math.sin(a.latitude*r)*Math.cos(b.latitude*r)*Math.cos(d)) / r + 360) % 360;
}
export function replayHeading(offset: number, route: RoutePoint[]) {
  if (route.length < 2) return 0;
  const index = replaySegmentIndex(route[0].timestamp + offset, route);
  // Preserve the last meaningful direction through stops; parked GPS headings
  // and bearings from tiny displacements are not evidence of a vehicle turn.
  for (let i = index; i >= 0; i--) {
    const a = route[i], b = route[i + 1];
    if (a.speed !== undefined && a.speed <= .5) continue;
    if (Number.isFinite(a.heading) && a.speed !== undefined && a.speed > .5) return ((a.heading! % 360) + 360) % 360;
    if (b && !b.gapBefore && metres(a, b) >= 3) return bearing(a, b);
  }
  return Number.isFinite(route[0].heading) ? route[0].heading! : 0;
}
